import CheckCircle from "@assets/images/check-circle.svg";
import InfoIcon from "@assets/images/info.png";
import XCircle from "@assets/images/x-circle.svg";
import { Box } from "@material-ui/core";
import { isAuthTokenExpired } from "@saleor/auth";
import { isStaffRole } from "@saleor/auth/hooks/useUserRole";
import { SubmitFilterDataType } from "@saleor/changeOrderDomestic/components/OrderFilterSection/constant";
import OrderListFilter from "@saleor/changeOrderDomestic/components/OrderListFilter";
import OrderListTable from "@saleor/changeOrderDomestic/components/OrderListTable";
import {
  DomesticOrderListSortField,
  DomesticOrderListUrl,
  ExporOrderListUrlQueryParams
} from "@saleor/changeOrderDomestic/urls";
import AppHeader from "@saleor/components/AppHeader";
import Container from "@saleor/components/Container";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import {
  getNotRefDraftOrderPath,
  orderCartPath
} from "@saleor/createOrder/urls";
import DialogError from "@saleor/createOrderExport/components/DialogError";
import {
  SapChangeOrderInput,
  useDuplicateOrderCipMutation,
  useDuplicateOrderMutation,
  useSapChangeOrderQuery,
  useScgpMeQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginationLocal from "@saleor/hooks/usePaginationLocal";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import isEmpty from "lodash/isEmpty";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import useRouter from "use-react-router";

export interface OrdersViewProps {
  params: ExporOrderListUrlQueryParams;
}

interface StyleProps {
  fullScreen?: boolean;
}

const useStyles = makeStyles<StyleProps>(
  theme => ({
    pageHeader: {
      marginBottom: "1.2rem",
      "& > :first-child": {
        visibility: props => (props.fullScreen ? "hidden" : "visible"),
        margin: 0,
        marginBottom: props => (props.fullScreen ? "0.4rem" : "1.2rem")
      },
      "& > :nth-child(2)": {
        alignSelf: "flex-end"
      }
    },
    searchBar: {
      display: props => (props.fullScreen ? "none" : "block")
    },
    flex: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    },
    inlineBlock: {
      display: "inline-block",
      color: theme.palette.grey[900]
    }
  }),
  { name: "OrdersView" }
);

function SectionMessage(props) {
  const classes = useStyles({});
  return (
    <Box className={classes.flex} data-testid="info-message">
      <img src={props.icon} alt="info" />
      <Box ml={1}>
        <MyTypography
          variant="body3"
          style={{
            color: props.color
          }}
        >
          {props.message}
        </MyTypography>
      </Box>
    </Box>
  );
}

const OrdersView: React.FC<OrdersViewProps> = ({ params }) => {
  const intl = useIntl();
  const [
    { fullScreen, filterOrder: filterAppState },
    dispatchAppState
  ] = useAppState();
  const [filter, setFilter] = useState<SubmitFilterDataType>({});
  const [isSearched, setIsSearched] = useState<boolean>(false);

  const [popUpContent, setPopUpContent] = useState(null);
  const [openError, setOpenError] = useState<boolean>(false);

  const isStaff = isStaffRole();
  const classes = useStyles({ fullScreen });
  const navigate = useNavigator();

  const { updateListSettings, settings } = useListSettings(
    ListViews.FILTER_ORDER
  );

  const { data: dataScgpMe } = useScgpMeQuery({
    skip: isAuthTokenExpired()
  });

  const { history } = useRouter();
  const handleBack = () => {
    history.push("/");
  };

  useEffect(() => {
    updateListSettings("rowNumber", 50);

    // SEO-8570 : Clear search result
    const params = new URLSearchParams(location.search);
    const filtered = params.get("filtered") === "true";

    // filter store
    if (filterAppState && filtered) {
      setFilter(filterAppState);
      setIsSearched(true);
    }

    if (!filtered) {
      dispatchAppState({
        type: "setFilterOrder",
        payload: {
          filterOrder: null
        }
      });
    }
  }, []);

  const prodErrorContent = (
    <>
      {" "}
      <MyTypography
        variant="body2"
        className={classes.inlineBlock}
        data-testid="prodErrorContent"
      >
        กรุณาเลือก{" "}
        <MyTypography variant="subtitle2" className={classes.inlineBlock}>
          สินค้ากลุ่มเดียวกัน
        </MyTypography>{" "}
        ในการสร้างคำสั่งซื้อ
      </MyTypography>
    </>
  );

  const [onDuplicateOrder] = useDuplicateOrderMutation({
    onCompleted(data) {
      if (data?.duplicateOrder?.order?.id) {
        navigate(orderCartPath(data?.duplicateOrder?.order?.id));
      } else if (
        data?.duplicateOrder?.errors[0]?.code === "PRODUCT_GROUP_ERROR"
      ) {
        setOpenError(true);
        setPopUpContent(prodErrorContent);
      }
    }
  });

  const [onDuplicateCipOrder] = useDuplicateOrderCipMutation({
    onCompleted(data) {
      if (data?.duplicateOrderCip?.order?.id) {
        navigate(getNotRefDraftOrderPath(data?.duplicateOrderCip?.order?.id));
      }
    }
  });

  const filterSAP: SapChangeOrderInput = useMemo(
    () => ({
      channel: isStaff ? "All" : "30",
      soldTo: filter.soldTo ? [filter.soldTo] : [""],
      materialNoMaterialDescription: filter.materialCodeName
        ? [filter.materialCodeName]
        : [""],
      saleOrderNo: filter.soNo || undefined,
      contractNo: filter.contractNo || undefined,
      dpNo: filter.dpNo || undefined,
      purchaseOrderNo: filter.poNo || undefined,
      invoiceNo: filter.invoiceNo || undefined,
      bu: filter.bu || dataScgpMe?.scgpMe?.extendData?.scgpBus[0]?.code,
      saleOrg: filter.company === "All" ? undefined : filter.company,
      saleGroup: filter.saleGroup === "All" ? undefined : filter.saleGroup,
      salesEmployeeNo: filter.saleEmployee || undefined,
      orderType: filter.orderType || "All",
      status: filter.statusSap || undefined,
      createDate: filter.createDate || undefined,
      lastUpdateDate: filter.lastUpdate || undefined,
      materialGroup1: filter?.materialGroup1,
      sourceOfApp: filter?.sourceOfApp || undefined
    }),
    [filter]
  );

  const { data: dataSAP, loading } = useSapChangeOrderQuery({
    skip: !isSearched || !Object.keys(filter).length,
    displayLoader: true,
    variables: {
      filter: filterSAP
    },
    sourceSystem: "SAP"
  });

  const listOrderSort =
    useMemo(() => {
      if (dataSAP?.sapChangeOrder) {
        const paramSort = getSortParams(params);
        const listToSort = [...dataSAP.sapChangeOrder];
        switch (paramSort.sort) {
          case DomesticOrderListSortField.piNo:
          default:
            if (paramSort.asc) {
              listToSort.sort((itemA, itemB) =>
                itemA.soNo.localeCompare(itemB.soNo)
              );
            } else {
              listToSort.sort((itemA, itemB) =>
                itemB.soNo.localeCompare(itemA.soNo)
              );
            }
            break;
          case DomesticOrderListSortField.poNo:
            if (paramSort.asc) {
              listToSort.sort((itemA, itemB) =>
                itemA.poNo.localeCompare(itemB.poNo)
              );
            } else {
              listToSort.sort((itemA, itemB) =>
                itemB.poNo.localeCompare(itemA.poNo)
              );
            }
            break;
          case DomesticOrderListSortField.contractNo:
            if (paramSort.asc) {
              listToSort.sort((itemA, itemB) =>
                itemA.contractNo.localeCompare(itemB.contractNo)
              );
            } else {
              listToSort.sort((itemA, itemB) =>
                itemB.contractNo.localeCompare(itemA.contractNo)
              );
            }
            break;
          case DomesticOrderListSortField.soldtoParty:
            if (paramSort.asc) {
              listToSort.sort((itemA, itemB) =>
                itemA.soldToParty.localeCompare(itemB.soldToParty)
              );
            } else {
              listToSort.sort((itemA, itemB) =>
                itemB.soldToParty.localeCompare(itemA.soldToParty)
              );
            }
            break;
          case DomesticOrderListSortField.createDate:
            if (paramSort.asc) {
              listToSort.sort(
                (itemA, itemB) =>
                  +new Date(
                    itemA.orderDate
                      .split("/")
                      .reverse()
                      .join("/")
                  ) -
                  +new Date(
                    itemB.orderDate
                      .split("/")
                      .reverse()
                      .join("/")
                  )
              );
            } else {
              listToSort.sort(
                (itemA, itemB) =>
                  +new Date(
                    itemB.orderDate
                      .split("/")
                      .reverse()
                      .join("/")
                  ) -
                  +new Date(
                    itemA.orderDate
                      .split("/")
                      .reverse()
                      .join("/")
                  )
              );
            }
        }
        return listToSort;
      }
      return [];
    }, [dataSAP?.sapChangeOrder, params]) || [];

  const {
    currentData,
    next,
    prev,
    toFirst,
    toLast,
    disableNext,
    disablePrev,
    lastestPageItemNumber
  } = usePaginationLocal(listOrderSort, settings.rowNumber);

  const handleSort = field => {
    toFirst();
    navigate(
      DomesticOrderListUrl({
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };

  useEffect(() => {
    toFirst();
  }, [settings?.rowNumber, filter]);

  const handleChangeFilter = (filterObj: SubmitFilterDataType) => {
    setFilter({ ...filterObj });
    if (!isEmpty(filterObj)) {
      setIsSearched(true);
      navigate(
        DomesticOrderListUrl({
          ...params,
          filtered: true
        }),
        { replace: true }
      );
    }
  };

  const message = useMemo(() => {
    if (loading) {
      return {
        icon: InfoIcon,
        message: intl.formatMessage({
          defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
        }),
        color: "#555555"
      };
    }
    if (!isSearched) {
      return {
        icon: InfoIcon,
        message: intl.formatMessage({
          defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
        }),
        color: "#555555"
      };
    }
    if (isSearched && !dataSAP?.sapChangeOrder?.length) {
      return {
        icon: XCircle,
        message: intl.formatMessage({
          defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
        }),
        color: "#FF2600"
      };
    }
    return {
      icon: CheckCircle,
      message: intl.formatMessage({
        defaultMessage: "พบรายการที่เกี่ยวข้อง"
      }),
      color: "#28A745"
    };
  }, [dataSAP?.sapChangeOrder?.length, isSearched, loading]);

  return (
    <>
      <AppHeader onBack={handleBack}>
        <MyTypography variant="body3">
          {intl.formatMessage({ defaultMessage: "หน้าหลัก" })}
        </MyTypography>
      </AppHeader>
      <Container>
        <PageHeader
          className={classes.pageHeader}
          title={intl.formatMessage({
            defaultMessage: "Change Order",
            description: "label"
          })}
        ></PageHeader>
        <OrderListFilter
          message={<SectionMessage {...message} />}
          handleChangeFilter={handleChangeFilter}
          listOrder={dataSAP?.sapChangeOrder}
          loading={loading}
          setIsSearched={setIsSearched}
        />
        {listOrderSort?.length || loading ? (
          <OrderListTable
            pageInfo={{
              hasPreviousPage: !disablePrev(),
              hasNextPage: !disableNext()
            }}
            data={currentData()}
            settings={settings}
            onDuplicateOrder={onDuplicateOrder}
            onDuplicateCipOrder={onDuplicateCipOrder}
            onUpdateListSettings={updateListSettings}
            totalCount={dataSAP?.sapChangeOrder?.length}
            latestPageItemNumber={lastestPageItemNumber}
            onPreviousFirstPage={toFirst}
            onNextLastPage={toLast}
            disabled={loading}
            onNextPage={next}
            onPreviousPage={prev}
            onRowClick={null}
            sort={getSortParams(params)}
            onSort={handleSort}
            filter={filter}
          />
        ) : (
          <></>
        )}
        <DialogError
          onClose={() => {
            setOpenError(false);
            setPopUpContent(null);
          }}
          open={openError}
          body={popUpContent}
        />
      </Container>
    </>
  );
};

export default OrdersView;
